const mounted = new WeakSet();
function node(tag, attrs = {}, children = []) {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === false || value == null) continue;
    if (key === 'text') el.textContent = value;
    else el.setAttribute(key, value === true ? '' : String(value));
  }
  for (const child of children) el.append(child);
  return el;
}
async function mount(root) {
  if (mounted.has(root)) return;
  mounted.add(root);
  const config = JSON.parse(root.querySelector('[data-advisor-config]').textContent);
  const engine = await import(config.engineUrl);
  const t = (key, args = {}) => Object.entries(args).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, String(value)), config.copy[key] || key);
  const url = path => `${config.rootUrl.replace(/\/$/, '')}/${String(path).replace(/^\//, '')}`;
  const results = root.querySelector('[data-results]');
  const panel = root.querySelector('[data-panel]');
  const tabs = [...root.querySelectorAll('[data-category]')];
  const cache = new Map();
  const intentByCategory = {};
  const advancedByCategory = {};
  let answers = engine.sanitizeAnswers({ needs: [], intent: 'unknown' });
  let category = null;
  let currentResult = null;
  let announcedIntent = '';
  let active = !root.closest('dialog') || root.closest('dialog').open;
  const money = price => {
    try { return new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(price / 100); }
    catch { return `${(price / 100).toFixed(2)} ${config.currency}`; }
  };
  const cardDescriptors = result => result.cards?.length ? result.cards : (result.products || []).map(id => ({ id, role: result.productDetails?.[id]?.role || 'candidate', reason: result.productDetails?.[id]?.reasons?.[0] || result.reason, tradeoff: result.productDetails?.[id]?.tradeoffs?.[0], checks: result.productDetails?.[id]?.checks || result.checks || [] }));
  function productCard(descriptor) {
    const product = config.products[descriptor.id];
    if (!product) return node('div');
    const state = cache.get(product.handle);
    const article = node('article', { class: `fg-rv__product fg-rv__product--${descriptor.role || 'candidate'}`, 'data-product-id': descriptor.id });
    const availabilityPending = state?.status === 'soldout' || state?.status === 'unavailable' || state?.preorder;
    const roleKey = availabilityPending ? 'v6_check_availability' : descriptor.role === 'primary' ? 'v6_primary' : descriptor.role === 'alternative' ? 'v6_alternative' : 'v6_candidate';
    article.append(node('p', { class: 'fg-rv__product-role', text: t(roleKey) }));
    const media = node('a', { class: 'fg-rv__product-media', href: url(`products/${product.handle}`), 'aria-label': product.name });
    if (state?.image && /^(https?:)?\/\//.test(state.image)) media.append(node('img', { src: state.image, alt: state.title || product.name, width: 480, height: 360, loading: 'lazy', decoding: 'async' }));
    else media.append(node('span', { class: 'fg-rv__image-placeholder', 'aria-hidden': 'true' }));
    const body = node('div', { class: 'fg-rv__product-body' }, [node('h4', { text: product.name }), node('p', { class: 'fg-rv__pitch', text: t(`quick_product_${descriptor.id}`) })]);
    if (!state || state.status === 'loading') body.append(node('p', { class: 'fg-rv__price fg-rv__price--loading', text: t('v6_fetching') }));
    else if (state.status === 'unavailable') {
      body.append(node('p', { class: 'fg-rv__fetch-error', text: t('v6_unavailable') }));
      const retry = node('button', { type: 'button', class: 'fg-rv__text-button', text: t('v6_retry') });
      retry.addEventListener('click', () => { cache.delete(product.handle); loadProducts(); });
      body.append(retry);
    } else {
      body.append(node('p', { class: 'fg-rv__price', text: t('v6_from', { price: money(state.price) }) }));
      if (state.status === 'soldout' || state.preorder) body.append(node('p', { class: 'fg-rv__stock', text: t(state.status === 'soldout' ? 'v6_soldout' : 'v6_preorder') }));
    }
    const explanation = node('div', { class: 'fg-rv__explanation' });
    if (descriptor.reason) explanation.append(node('p', {}, [node('strong', { text: t('v6_why') }), node('span', { text: t(descriptor.reason) })]));
    if (descriptor.tradeoff) explanation.append(node('p', {}, [node('strong', { text: t('v6_tradeoff') }), node('span', { text: t(descriptor.tradeoff) })]));
    body.append(explanation);
    const checks = [...new Set(descriptor.checks || currentResult?.checks || [])];
    if (checks.length) body.append(node('details', { class: 'fg-rv__card-checks' }, [node('summary', { text: t('v6_check') }), node('ul', {}, checks.map(key => node('li', { text: t(key) })))]));
    body.append(node('a', { class: 'fg-rv__product-link', href: url(`products/${product.handle}`), text: t(state?.status === 'soldout' ? 'v6_view_details' : 'v6_view_options') }));
    article.append(media, body);
    return article;
  }
  function repaintProduct(handle) {
    if (!currentResult) return;
    for (const descriptor of cardDescriptors(currentResult)) {
      if (config.products[descriptor.id]?.handle !== handle) continue;
      const existing = results.querySelector(`[data-product-id="${descriptor.id}"]`);
      if (!existing) continue;
      const checksOpen = existing.querySelector('details')?.open;
      const focused = existing.contains(document.activeElement);
      const focusedKind = focused ? document.activeElement.matches('.fg-rv__product-link') ? '.fg-rv__product-link' : document.activeElement.matches('.fg-rv__product-media') ? '.fg-rv__product-media' : document.activeElement.matches('summary') ? 'summary' : null : null;
      const replacement = productCard(descriptor);
      if (checksOpen && replacement.querySelector('details')) replacement.querySelector('details').open = true;
      existing.replaceWith(replacement);
      if (focusedKind) replacement.querySelector(focusedKind)?.focus({ preventScroll: true });
      else if (focused) { replacement.tabIndex = -1; replacement.focus({ preventScroll: true }); }
    }
  }
  function loadProducts() {
    if (!active || !currentResult) return;
    for (const id of currentResult.products || []) {
      const product = config.products[id];
      if (!product || cache.has(product.handle)) continue;
      cache.set(product.handle, { status: 'loading' });
      repaintProduct(product.handle);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      fetch(url(`products/${encodeURIComponent(product.handle)}.js`), { credentials: 'same-origin', signal: controller.signal })
        .then(response => { if (!response.ok) throw new Error('unavailable'); return response.json(); })
        .then(data => cache.set(product.handle, engine.selectProductState(data, product.handle)))
        .catch(() => cache.set(product.handle, { status: 'unavailable' }))
        .finally(() => { clearTimeout(timer); if (root.isConnected) repaintProduct(product.handle); });
    }
  }
  function renderResults() {
    const all = engine.buildRecommendations(answers);
    currentResult = all.find(result => result.category === category || result.shared?.includes(category)) || null;
    results.replaceChildren();
    if (!currentResult) return;
    const cards = cardDescriptors(currentResult);
    const questions = engine.getQuestions(answers, category);
    // Retained category answers can still filter products when intent hides a question.
    const detailKeys = new Set([...questions.primary, ...questions.advanced].map(field => field.id || field.key));
    for (const key of Object.keys(answers)) if (key.startsWith(`${category}_`)) detailKeys.add(key);
    const hasDetails = [...detailKeys].some(key => {
      const value = answers[key];
      return value != null && value !== 'unknown';
    });
    const headingKey = !cards.length ? 'v6_no_match' : !hasDetails ? 'v62_featured' : currentResult.status === 'shortlist' ? 'v6_shortlist' : 'v6_candidates';
    const heading = node('div', { class: 'fg-rv__result-heading' }, [node('p', { class: 'fg-rv__result-update', text: t(hasDetails ? 'v62_result_updated' : 'v62_live_hint') }), node('h3', { text: t(headingKey) }), node('span', { text: t('v6_pending') })]);
    results.append(heading);
    if (!hasDetails && cards.length) results.append(node('p', { class: 'fg-rv__result-guide', text: t(answers.intent !== 'unknown' ? 'v62_result_next' : 'v62_result_start') }));
    if (cards.length && currentResult.reason && currentResult.reason !== currentResult.warning) results.append(node('p', { class: 'fg-rv__result-context', text: t(currentResult.reason) }));
    if (currentResult.warning) results.append(node('p', { class: 'fg-rv__warning', role: 'note', text: t(currentResult.warning) }));
    if (cards.length) {
      results.append(node('div', { class: 'fg-rv__product-grid' }, cards.map(productCard)), node('p', { class: 'fg-rv__price-note', text: t('v6_price_note') }));
    } else {
      if (currentResult.reason && currentResult.reason !== currentResult.warning) results.append(node('p', { class: 'fg-rv__no-match-reason', text: t(currentResult.reason) }));
      if (currentResult.checks?.length) results.append(node('ul', { class: 'fg-rv__no-match-checks' }, currentResult.checks.map(key => node('li', { text: t(key) }))));
      results.append(node('a', { class: 'fg-rv__product-link fg-rv__contact-link', href: url('pages/contact'), text: t('v6_contact') }));
    }
    results.append(node('a', { class: 'fg-rv__browse', href: url(config.categories[category]), text: t('v6_browse', { category: t(`v6_${category}`).toLowerCase() }) }));
    const intentSignature = `${category}:${answers.intent || 'unknown'}`;
    const intentContext = engine.getIntentContext?.(answers, category);
    root.querySelector('[data-status]').textContent = intentContext && announcedIntent !== intentSignature ? `${t(intentContext.titleKey)}. ${t(intentContext.bodyKey)} ${t('v6_updated')}` : t('v6_updated');
    announcedIntent = intentSignature;
    loadProducts();
  }
  function question(descriptor) {
    const key = descriptor.id || descriptor.key;
    const id = `${root.id}-${key}`;
    const wrapper = node('div', { class: 'fg-rv__question', 'data-question': key });
    const label = node('label', { for: id, text: t(descriptor.labelKey) });
    const select = node('select', { name: key, id, 'data-answer': key, 'aria-describedby': descriptor.helpKey ? `${id}-help` : null });
    for (const option of descriptor.options) select.append(node('option', { value: option.value, selected: (answers[key] ?? 'unknown') === option.value, text: t(option.labelKey) }));
    select.addEventListener('change', () => {
      answers = engine.updateAnswer(answers, key, select.value);
      renderQuestions(); renderResults();
      root.querySelector(`[data-answer="${key}"]`)?.focus({ preventScroll: true });
    });
    wrapper.append(label, select);
    if (descriptor.helpKey) wrapper.append(node('p', { class: 'fg-rv__question-help', id: `${id}-help`, text: t(descriptor.helpKey) }));
    return wrapper;
  }
  function renderQuestions() {
    const context = engine.getIntentContext?.(answers, category);
    const contextRegion = root.querySelector('[data-intent-context]');
    if (contextRegion) {
      contextRegion.hidden = !context;
      contextRegion.replaceChildren(...(context ? [node('h3', { text: t(context.titleKey) }), node('p', { text: t(context.bodyKey) })] : []));
    }
    const descriptors = engine.getQuestions(answers, category);
    root.querySelector('[data-primary-questions]').replaceChildren(...descriptors.primary.slice(0, 3).map(question));
    root.querySelector('[data-advanced-questions]').replaceChildren(...descriptors.advanced.map(question));
    root.querySelector('[data-advanced]').hidden = !descriptors.advanced.length;
  }
  function renderIntent() {
    const parent = root.querySelector('[data-intent-options]');
    parent.replaceChildren();
    for (const value of ['replace', 'upgrade', 'new']) {
      const input = node('input', { type: 'radio', name: `${root.id}-intent`, value, checked: answers.intent === value });
      input.addEventListener('change', () => {
        intentByCategory[category] = value;
        answers = engine.updateAnswer(answers, 'intent', value);
        renderQuestions(); renderResults();
      });
      parent.append(node('label', { class: 'fg-rv__intent-option' }, [input, node('span', { text: t(`v6_intent_${value}`) })]));
    }
  }
  function chooseCategory(next, focus = false) {
    if (!config.categories[next]) return;
    if (category) advancedByCategory[category] = root.querySelector('[data-advanced]').open;
    category = next;
    answers = engine.sanitizeAnswers({ ...answers, needs: [next], intent: intentByCategory[next] || 'unknown' });
    for (const tab of tabs) { tab.setAttribute('aria-selected', String(tab.dataset.category === next)); tab.tabIndex = tab.dataset.category === next ? 0 : -1; }
    const tab = tabs.find(item => item.dataset.category === next);
    panel.setAttribute('aria-labelledby', tab.id);
    panel.hidden = false; root.querySelector('[data-empty]').hidden = true;
    renderIntent(); renderQuestions(); renderResults();
    root.querySelector('[data-advanced]').open = Boolean(advancedByCategory[next]);
    if (focus) tab.focus({ preventScroll: true });
  }
  for (const [index, tab] of tabs.entries()) {
    tab.addEventListener('click', () => { active = true; chooseCategory(tab.dataset.category); });
    tab.addEventListener('keydown', event => {
      let next = null;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      if (next !== null) { event.preventDefault(); active = true; chooseCategory(tabs[next].dataset.category, true); }
    });
  }
  root.querySelector('[data-reset]').addEventListener('click', () => {
    const questionSet = engine.getQuestions(answers, category);
    const resetKeys = new Set([...questionSet.primary, ...questionSet.advanced].map(field => field.id || field.key));
    for (const key of Object.keys(answers)) if (key.startsWith(`${category}_`)) resetKeys.add(key);
    if (category === 'cook') resetKeys.add('hood_width');
    for (const key of resetKeys) {
      const sharedPower = ['ac_power', 'propane', 'dc_power'].includes(key);
      const belongsElsewhere = engine.CATEGORY_IDS.some(other => other !== category && key.startsWith(`${other}_`));
      if (!sharedPower && !belongsElsewhere) delete answers[key];
    }
    delete intentByCategory[category];
    answers = engine.sanitizeAnswers({ ...answers, intent: 'unknown' });
    renderIntent(); renderQuestions(); renderResults();
  });
  document.addEventListener('fogatti:advisor:open', event => {
    if (event.target === root || event.target.contains(root)) { active = true; loadProducts(); }
  });
  root.querySelector('[data-fallback]').hidden = true;
  const matched = Object.entries(config.products).find(([, product]) => product.handle === root.dataset.productHandle)?.[0];
  const contextText = `${root.dataset.productHandle || ''} ${root.dataset.productType || ''}`.toLowerCase();
  const contextCategory = matched?.startsWith('ac_') ? 'cool' : matched?.startsWith('heat_') ? 'warm' : matched?.startsWith('wh_') ? 'water' : matched?.startsWith('kitchen_') || matched?.startsWith('hood_') ? 'cook' : /instashower|hybridshower|water[ -]?heater|tankless/.test(contextText) ? 'water' : /instacool|air[ -]?conditioner/.test(contextText) ? 'cool' : /instaheat|furnace/.test(contextText) ? 'warm' : /cooktop|induction|range[ -]?(hood|oven)|oven[ -]?stove/.test(contextText) ? 'cook' : null;
  const earlyCategory = root.dataset.pendingCategory;
  if (earlyCategory || contextCategory) chooseCategory(earlyCategory || contextCategory);
  root.dispatchEvent(new CustomEvent('fogatti:advisor:ready', { bubbles: true }));
}
function initialize(scope = document) {
  for (const root of scope.querySelectorAll('[data-fg-rv-advisor]')) {
    if (!mounted.has(root)) for (const button of root.querySelectorAll('[data-category]')) button.addEventListener('click', () => { root.dataset.pendingCategory = button.dataset.category; });
    mount(root).catch(() => {
      const fallback = root.querySelector('[data-fallback]');
      if (fallback) fallback.hidden = false;
      root.querySelector('[data-panel]').hidden = true;
      root.querySelector('[data-empty]').hidden = false;
    });
  }
}
initialize();
document.addEventListener('shopify:section:load', event => initialize(event.target));
